<script>
    import Checkbox from '../checkbox/Checkbox.svelte';
    import ContentLabel from '../contentLabel/ContentLabel.svelte';
    import RadioButton from '../radioButton/RadioButton.svelte';

    let {
        // Array of objects with either 'label' or 'model' in each object.
        // If 'model' is present, ContentLabel is used to render the model.
        // For multi select, the 'isChecked' attribute sets the selected state.
        // Attributes 'subText', 'rightAlignedText', 'isDisabled' are passed on
        // to the RadioButton or Checkbox.
        list: initialList,

        // If true, use checkboxes. If false, use radio buttons.
        isMultiSelect = false,

        // For radio buttons, index of the button that is selected
        selectedIndex = $bindable(null),

        // For checkboxes, show an option to select all, and label for it
        showSelectAllInput = false,
        selectAllText = window.i18n.gettext('Select all items'),

        // Compact layout: use less whitespace around inputs. For example in sidebar.
        isCompact = false,

        // Function to execute when the selection changes
        callback = () => {},

        // Message to show if the list has a size of 0
        emptyStateMessage = window.i18n.gettext('Empty')
    } = $props()

    const cid = _.uniqueId()

    // Make list reactive and fill it with the initial list
    // svelte-ignore state_referenced_locally
    let list = $state(initialList)

    // For checkboxes with select all, we keep a boolean with the select all state
    const allSelected = $derived(
        isMultiSelect &&
        showSelectAllInput &&
        list.some((item) => item.isChecked) &&
        list.every((item) => item.isDisabled || item.isChecked)
    )

    // Select all should only be enabled if there is at least one selectable item
    const hasSelectableItem = $derived(list.some((item) => !item.isDisabled))

    export function getSelection() {
        if (!isMultiSelect) {
            return $state.snapshot(list[selectedIndex])
        }

        return $state.snapshot(list).filter((item) => {
            return item.isChecked
        })
    }

    export function getList() {
        return $state.snapshot(list)
    }

    export function setList(newList) {
        list = newList
    }

    function onSelectAll(state) {
        for (const item of list) {
            if (!item.isDisabled) {
                item.isChecked = state
            }
        }
        callback()
    }

    function onSelectCheckbox() {
        callback()
    }

    function onSelectRadioButton(index) {
        selectedIndex = index
        callback(index)
    }

</script>

<div class="chooser" class:is-compact={isCompact}>
    {#if showSelectAllInput && isMultiSelect && list.length > 1}
        <Checkbox
            parentGroup={cid}
            label={selectAllText}
            value="ALL"
            isChecked={allSelected}
            isDisabled={!hasSelectableItem}
            callback={onSelectAll}
        ></Checkbox>
    {/if}

    {#each list as item, index ((item.model?.cid || item.label) + index)}

        {#if isMultiSelect}

            <Checkbox
                parentGroup={cid}
                value={index}
                rightAlignedText={item.rightAlignedText}
                subText={item.subText}
                isDisabled={item.isDisabled}
                onClickDisabled={item.onClickDisabled}
                bind:isChecked={item.isChecked}
                callback={onSelectCheckbox}>

                {#snippet label()}
                    {#if item.labelSnippet}
                        {@render item.labelSnippet(item)}
                    {:else if item.model}
                        <ContentLabel model={item.model}></ContentLabel>
                    {:else}
                        {item.label}
                    {/if}
                {/snippet}

            </Checkbox>

        {:else}

            <RadioButton
                parentGroup={cid}
                value={index}
                isChecked={index === selectedIndex}
                rightAlignedText={item.rightAlignedText}
                subText={item.subText}
                isDisabled={item.isDisabled}
                callback={onSelectRadioButton}>

                {#snippet label()}
                    {#if item.labelSnippet}
                        {@render item.labelSnippet(item)}
                    {:else if item.model}
                        <ContentLabel model={item.model}></ContentLabel>
                    {:else}
                        {item.label}
                    {/if}
                {/snippet}

            </RadioButton>

        {/if}

    {:else}

        <div class="empty">{emptyStateMessage}</div>

    {/each}

</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.chooser > :global(label) {
  display: flex;
  padding: 16px 24px;
  background-color: #fff;
}
.chooser > :global(label):has(:global(input[value='ALL'])) {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: hsl(27, 15%, 93%);
}
.chooser > :global(label):hover {
  background-color: hsl(40, 20%, 96%);
}
.chooser > :global(label):not(:last-of-type) {
  border-bottom: 1px solid hsl(27, 15%, 93%);
}
.chooser .empty {
  padding: 16px 24px;
  background-color: #fff;
}
.chooser.is-compact > :global(label) {
  padding: 12px 16px;
}
.chooser.is-compact .empty {
  padding: 12px 16px;
}</style>
